import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format a number as currency
export function formatCurrency(value: number | string | undefined) {
  if (value === undefined || value === null) return "0,00 €";
  
  let numValue: number;
  
  if (typeof value === 'string') {
    // Handle German number format (comma as decimal separator)
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    numValue = parseFloat(cleanValue);
  } else {
    numValue = value;
  }
  
  if (isNaN(numValue)) return "0,00 €";
  
  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(numValue);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return numValue.toFixed(2).replace('.', ',') + ' €';
  }
}

// Format a date with spaces (DD. MM. YYYY)
export function formatDate(dateStr: string) {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr);
    const formatted = new Intl.DateTimeFormat('de-DE').format(date);
    // Add spaces after dots: "03.09.2024" -> "03. 09. 2024"
    return formatted.replace(/\./g, '. ').replace(/\. $/, '');
  } catch (error) {
    console.error('Invalid date:', dateStr);
    return '';
  }
}

// Handle debounce for search input
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...funcArgs: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
  };
}

// Generate file blob URL
export function generateBlobUrl(blob: Blob): string {
  return URL.createObjectURL(blob);
}

// Clean up blob URL when no longer needed
export function cleanupBlobUrl(url: string): void {
  URL.revokeObjectURL(url);
}

// Generate default quote number
export function generateQuoteNumber(): string {
  const now = new Date();
  const year = now.getFullYear();
  const randomNumber = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `A-${year}-${randomNumber}`;
}

// Check if object is empty
export function isEmpty(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 0;
}
